import { useMemo, useState } from 'react';

import { type APIFilterState, useApiController } from '@/_containers';

import type { AdvanceFilter } from './types';

export function useAdvanceFilters<T>(initialFilters: AdvanceFilter<T>[], initAPIController?: Partial<APIFilterState>) {
  const filters = useApiController(initAPIController);
  const [advFiltersState, setAdvFiltersState] = useState(initialFilters);

  const handleSubmitAdvFilters = (f: AdvanceFilter<T>) => {
    const { applied, mainKey, subKey, subKey2, valueToApply, additionalFilterValue, filterValue, selectedType, title } =
      f;

    setAdvFiltersState((prev) => {
      const newArr = [...prev];
      const index = newArr.findIndex(
        (f) =>
          f.title === title &&
          f.mainKey === mainKey &&
          (f?.subKey ?? '') === (subKey ?? '') &&
          (f?.subKey2 ?? '') === (subKey2 ?? '')
      );

      if (index !== -1) {
        newArr[index] = {
          ...newArr[index],
          applied,
          valueToApply: additionalFilterValue ? additionalFilterValue + valueToApply : valueToApply,
          filterValue,
          selectedType,
        };
      } else {
        console.warn('filter not found', { mainKey, subKey, subKey2 });
      }

      return newArr;
    });
  };

  const handleClearAllAdvFilters = () => {
    advFiltersState.forEach((f) => {
      f.applied = false;
      f.valueToApply = '';
      f.filterValue = '';
      f.selectedType = { name: '', value: '' };
    });

    setAdvFiltersState([...advFiltersState]);
  };

  const mergeFilters = (apiParamStr: string, advFilterStr: string) => {
    const arr = [apiParamStr, advFilterStr].filter((f) => f.length > 0);
    return arr.join('&');
  };

  const shouldUseAdvFilters = useMemo(() => {
    return advFiltersState.some((f) => f.applied);
  }, [advFiltersState]);

  const { allFiltersStr, advFilterStr } = useMemo(() => {
    const { apiParamStr } = filters;
    const appliedFilters = advFiltersState.filter((f) => f.applied);

    const advFilterStr = shouldUseAdvFilters
      ? `filter[advance_filtering]=${appliedFilters.map((f) => f.valueToApply).join(';')}`
      : '';

    return { allFiltersStr: mergeFilters(apiParamStr, advFilterStr), advFilterStr };
  }, [advFiltersState, filters, shouldUseAdvFilters]);

  return {
    ...filters,
    advFilterStr,
    allFiltersStr,
    shouldUseAdvFilters,
    mergeFilters,
    handleSubmitAdvFilters,
    handleClearAllAdvFilters,
    advFiltersState,
  };
}

export type UseAdvanceFiltersReturn<T> = ReturnType<typeof useAdvanceFilters<T>>;
